import { setMonth, setYear, format, startOfMonth, getDaysInMonth, startOfDay, setDate, getDate, getMonth, getYear, addMonths, setDay, differenceInDays, addDays, startOfWeek, isSameDay, isBefore, isAfter } from 'date-fns';
import { Observable, animationFrameScheduler, EMPTY } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { createComponent as createComponent$1 } from '@angular/core';

class CdsHighlightableComponent {
    addHighlightedClass(element) {
        element.classList.add('-highlighted');
    }
    removeHighlightedClass(element) {
        element.classList.remove('-highlighted');
    }
    scrollIntoView(element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

/**
 * Abstract class for components that are traversable, via keyboard, within the context of a parent list or group.
 */
class CdsTraversableComponent {
}

/**
 * ### Description
 * An object representing the configuration for filtering a field in an array of data.
 */
class CdsDataFilter {
}

/**
 * @internal
 * Flattens objects. Use with Array.prototype.reduce()
 */
function flatten(selfFn, flattenChildrenFn) {
    return (arr, current) => {
        const self = selfFn(current);
        const children = flattenChildrenFn(current);
        return arr.concat(self, ...children);
    };
}

/**
 * ### Description
 * An object representing the configuration for paging through an array of data.
 */
class CdsDataPage {
}

/* eslint-disable no-shadow */
var CdsSortDirection;
(function (CdsSortDirection) {
    CdsSortDirection[CdsSortDirection["None"] = 0] = "None";
    CdsSortDirection[CdsSortDirection["Ascending"] = 1] = "Ascending";
    CdsSortDirection[CdsSortDirection["Descending"] = -1] = "Descending";
})(CdsSortDirection || (CdsSortDirection = {}));

/**
 * ### Description
 * An object representing the configuration for sorting a field in an array of data.
 */
class CdsDataSort {
}

/* eslint-disable max-classes-per-file */
class CdsCalendarDate {
}
;

function combineDateparts(day, month, year) {
    // Set the month
    let date = setMonth(setYear(new Date(), Number(year)), month);
    // Handle the case where the selected date has been cleared, but the user decides to select another month/year etc without
    // selecting a day first.
    if (day === null) {
        day = parseInt(format(startOfMonth(date), 'dd'), 10);
    }
    // Handle cases such as Jan (31 days) to Feb (28/29 days) or Oct (31) to Nov (30)
    if (getDaysInMonth(date) < day) {
        day = getDaysInMonth(date);
    }
    // Set the year
    date = startOfDay(setDate(date, day));
    return date;
}
function getDateParts(date) {
    return {
        day: getDate(date),
        month: getMonth(date),
        year: getYear(date)
    };
}
function getWeekDays(locale) {
    const weekdays = [{
            name: 'Sunday',
            abbr: 'S'
        }, {
            name: 'Monday',
            abbr: 'M'
        }, {
            name: 'Tuesday',
            abbr: 'T'
        }, {
            name: 'Wednesday',
            abbr: 'W'
        }, {
            name: 'Thursday',
            abbr: 'T'
        }, {
            name: 'Friday',
            abbr: 'F'
        }, {
            name: 'Saturday',
            abbr: 'S'
        }];
    return weekdays.concat(weekdays.splice(0, getLocaleWeekStart(locale)));
}
function getMonths() {
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .map((value, index) => ({ label: value, key: index }));
}
function getYears(min, max) {
    const years = [];
    let start = min || 1990;
    const end = max || 2050;
    while (start <= end) {
        years.push({ label: `${start}`, key: start });
        start += 1;
    }
    return years;
}
function calculateCalendarDates(forMonth, range, locale) {
    const currentMonths = Array.from({ length: range }, (v, k) => startOfMonth(addMonths(forMonth, k)));
    // const currentMonth = startOfMonth(forMonth);
    /*
      Decide whether to start the calendar on the previous week
      (i.e. more than 7 days before the first day of the first week in the month)
    */
    const previousWeekStartBeforeMonthStart = setDay(currentMonths[0], getLocaleWeekStart(locale) - 7);
    const firstCalendarWeek = setDay(currentMonths[0], getLocaleWeekStart(locale));
    const calendarStart = differenceInDays(currentMonths[0], previousWeekStartBeforeMonthStart) > 7 ?
        firstCalendarWeek : previousWeekStartBeforeMonthStart;
    const calendarEnd = addDays(calendarStart, (42 * range) - 1);
    const previousMonth = startOfMonth(calendarStart);
    const previousMonthOffset = differenceInDays(calendarStart, previousMonth);
    const nextMonth = startOfMonth(calendarEnd);
    const previousCalendarDates = Array.from({ length: getDaysInMonth(previousMonth) - previousMonthOffset }, (v, k) => getDateObject(setDate(previousMonth, k + previousMonthOffset + 1), 'previous'));
    const currentCalendarDates = currentMonths.map(currentMonth => Array.from({ length: getDaysInMonth(currentMonth) }, (v, k) => getDateObject(setDate(currentMonth, k + 1), 'current'))).reduce((acc, current) => acc.concat(current), []);
    const nextCalendarDates = Array.from({ length: differenceInDays(calendarEnd, nextMonth) + 1 }, (v, k) => getDateObject(setDate(nextMonth, k + 1), 'next'));
    return [...previousCalendarDates, ...currentCalendarDates, ...nextCalendarDates];
}
function calculateCalendarDatesForWeek(from, range) {
    return Array.from({ length: range }, (v, k) => getDateObject(addDays(from, k + 1)));
}
function calculateCalendarWeekdays(from) {
    const weekstart = startOfWeek(from || new Date(), { weekStartsOn: getLocaleWeekStart('en') });
    return Array.from({ length: 7 }, (_, index) => {
        const dateObject = addDays(weekstart, index);
        return {
            dateObject,
            dayOfMonth: getDate(dateObject),
            dayOfWeek: format(dateObject, 'EEEE'),
            today: isSameDay(new Date(), dateObject)
        };
    });
}
function getLocaleWeekStart(locale) {
    return 0; // Sunday
}
function getDateObject(date, designation) {
    const today = new Date();
    return {
        dateObject: date,
        value: getDate(date),
        month: {
            index: getMonth(date),
            name: format(date, 'MMM')
        },
        year: getYear(date),
        designation,
        get today() {
            return isSameDay(date, today);
        }
    };
}
function getDateObjectWithHours(date) {
    return {
        ...date,
        hours: Array.from({ length: 24 }, (_, index) => index)
    };
}
const dateComparatorFn = (value1, value2, sortDirection) => {
    if (value1 && !value2) {
        return -1;
    }
    else if (!value1 && value2) {
        return 1;
    }
    else if (isBefore(value1, value2)) {
        return -1 * sortDirection;
    }
    else if (isAfter(value1, value2)) {
        return 1 * sortDirection;
    }
    return 0;
};

function convertToPixels(supplied = '', whole) {
    if (!supplied) {
        return 0;
    }
    const pixels = supplied.split('px');
    if (pixels.length > 1) {
        return +pixels[0];
    }
    const percentage = supplied.split('%');
    if (percentage.length > 1 && whole) {
        return (+percentage[0] / 100) * whole;
    }
}

function ResizeObserverSubject(element, debounce = 0, $cancelSubscriptions = EMPTY) {
    return new Observable((subscriber) => {
        const resizeObserver = new ResizeObserver(event => subscriber.next(event));
        resizeObserver.observe(element);
        return () => {
            resizeObserver.unobserve(element);
            resizeObserver.disconnect();
        };
    })
        .pipe(takeUntil($cancelSubscriptions), debounceTime(debounce, animationFrameScheduler));
}

function MutationObserverSubject(element, timeout = 0, $cancelSubscriptions = EMPTY) {
    return new Observable((subscriber) => {
        const mutationObserver = new MutationObserver(event => subscriber.next(event));
        mutationObserver.observe(element, { childList: true, subtree: true });
        return () => {
            mutationObserver.disconnect();
        };
    })
        .pipe(takeUntil($cancelSubscriptions), debounceTime(timeout, animationFrameScheduler));
}

/**
 * Utility service for creating dynamic components.
 */
function appendComponentToBody(componentRef, appRef) {
    appRef.attachView(componentRef.hostView);
    document.body.appendChild(componentRef.location.nativeElement);
    return componentRef;
}
function removeComponentFromBody(component, appRef) {
    component.location.nativeElement.remove();
    appRef.detachView(component.hostView);
    component.destroy();
}
/**
 *
 * Note that this does not attach the component to a ViewContainerRef, so change detection and lifecycle hooks will not be run.
 * You will need to attach the component to a ViewContainerRef or an AppRef manually.
 */
function createComponent(component, environmentInjector, elementInjector, ...content) {
    const projectableNodes = content.length > 0
        ? getContent(environmentInjector, elementInjector, ...content)
        : [];
    const componentRef = createComponent$1(component, { environmentInjector, elementInjector, projectableNodes });
    return componentRef;
}
function getContent(environmentInjector, elementInjector, ...args) {
    const projectableNodes = [];
    args.forEach((content) => {
        if (typeof content === 'string') {
            const element = document.createTextNode(content);
            projectableNodes.push([element]);
            return;
        }
        if (content instanceof Element) {
            projectableNodes.push([content]);
            return;
        }
        if (content instanceof Array) {
            const templateRef = content[0];
            const context = content[1];
            const viewRef = templateRef.createEmbeddedView(context);
            viewRef.detectChanges();
            projectableNodes.push(viewRef.rootNodes);
            return;
        }
        // default - create a component
        const componentRef = createComponent$1(content, { environmentInjector, elementInjector });
        projectableNodes.push([componentRef.location.nativeElement]);
    });
    return projectableNodes;
}

class CdsInputValidationOptions {
}

class CdsInputConfig extends CdsInputValidationOptions {
}

/**
 * Error object returned by the GridKeyManager when an attempt is made to focus an out-of-bounds item.
 */
class GridThresholdWarning {
}

function isNavigationKey(key) {
    return (key === 'Up' ||
        key === 'ArrowUp' ||
        key === 'Down' ||
        key === 'ArrowDown' ||
        key === 'Left' ||
        key === 'ArrowLeft' ||
        key === 'Right' ||
        key === 'ArrowRight');
}

function pluralize(value) {
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    if (value) {
        if (value.charAt(value.length - 1) === 'y' && !vowels.includes(value.charAt(value.length - 2))) {
            return value.substring(0, value.length - 1) + 'ies';
        }
        return value + 's';
    }
    return value;
}

function getOptionsFromColumn(key, data, multi = false) {
    const selectAll = {
        key: '',
        label: `Select All ${pluralize(key)}`
    };
    const entries = new Set();
    data.forEach(entry => entries.add(entry[key]));
    const options = Array.from(entries.values())
        .map(entry => ({ key: entry, label: entry, value: multi ? true : entry }));
    return multi ? options : [selectAll, ...options];
}

/* eslint-disable max-classes-per-file */
/**
 * An object representing an entry in a dropdown list.
 */
class CdsListItem {
}
/**
 * ### Description
 * (deprecated) An object representing a list item with children.
 */
// eslint-disable-next-line max-classes-per-file
class CdsGroupedListItem extends CdsListItem {
}

class FixedPositionStrategy {
    constructor(config) {
        this.config = config;
    }
}

function getQuadrant(anchorCoordinates) {
    const horizontalDivider = document.documentElement.clientHeight * 0.5;
    const verticalDivider = document.documentElement.clientWidth * 0.5;
    if (anchorCoordinates.top < horizontalDivider &&
        anchorCoordinates.left < verticalDivider) {
        return 1;
    }
    if (anchorCoordinates.top < horizontalDivider &&
        anchorCoordinates.left > verticalDivider) {
        return 2;
    }
    if (anchorCoordinates.top > horizontalDivider &&
        anchorCoordinates.left < verticalDivider) {
        return 3;
    }
    if (anchorCoordinates.top > horizontalDivider &&
        anchorCoordinates.left > verticalDivider) {
        return 4;
    }
    return 1;
}

class DirectionalPositionStrategy extends FixedPositionStrategy {
    constructor(direction, config) {
        super(config);
        this.direction = direction;
    }
    position(anchor) {
        const rect = anchor instanceof Element
            ? anchor.getBoundingClientRect()
            : new DOMRect(anchor.x, anchor.y);
        let styles = {};
        const quadrant = getQuadrant(rect);
        switch (this.direction) {
            case 'top':
                styles = {
                    ...styles,
                    ...getStylesForTop(rect, quadrant)
                };
                break;
            case 'right':
                styles = {
                    ...styles,
                    ...getStylesForRight(rect, quadrant)
                };
                break;
            case 'left':
                styles = {
                    ...styles,
                    ...getStylesForLeft(rect, quadrant)
                };
                break;
            case 'bottom':
                styles = {
                    ...styles,
                    ...getStylesForBottom(rect, quadrant)
                };
                break;
        }
        const adjacent = this.direction === 'left' || this.direction === 'right';
        const y = quadrant === 1 || quadrant === 2 ? 'bottom' : 'top';
        const x = quadrant === 1 || quadrant === 3 ? 'right' : 'left';
        return {
            anchor,
            styles: {
                ...styles,
            },
            options: {
                position: this.direction,
                direction: this.direction === 'top' || this.direction === 'bottom'
                    ? x : y,
                quadrant
            }
        };
    }
}
function getStylesForTop({ left, right, top }, quadrant) {
    return {
        bottom: `${document.documentElement.clientHeight - top}px`,
        left: quadrant === 1 || quadrant === 3 ?
            `${left}px` : '',
        right: quadrant === 2 || quadrant === 4 ?
            `${document.documentElement.clientWidth - right}px` : '',
        maxHeight: `${top * 0.9}px`,
        maxWidth: `${quadrant === 1 || quadrant === 3 ?
            (document.documentElement.clientWidth - left) * 0.9 :
            (document.documentElement.clientWidth - right) * 0.9}px`,
    };
}
function getStylesForRight({ right, top, bottom }, quadrant) {
    return {
        left: `${right}px`,
        top: quadrant === 1 || quadrant === 2 ?
            `${top}px` : '',
        bottom: quadrant === 3 || quadrant === 4 ?
            `${document.documentElement.clientHeight - bottom}px` : '',
        maxHeight: `${quadrant === 1 || quadrant === 2
            ? top * 0.9
            : bottom * 0.9}px`,
        maxWidth: `${document.documentElement.clientWidth - right * 0.9}px`,
    };
}
function getStylesForLeft({ left, top, bottom }, quadrant) {
    return {
        right: `${document.documentElement.clientWidth - left}px`,
        top: quadrant === 1 || quadrant === 2 ?
            `${top}px` : '',
        bottom: quadrant === 3 || quadrant === 4 ?
            `${document.documentElement.clientHeight - bottom}px` : '',
        maxHeight: `${quadrant === 1 || quadrant === 2
            ? top * 0.9
            : bottom * 0.9}px`,
        maxWidth: `${left * 0.9}px`,
    };
}
function getStylesForBottom({ left, right, bottom }, quadrant) {
    return {
        top: `${bottom}px`,
        left: quadrant === 1 || quadrant === 3 ?
            `${left}px` : '',
        right: quadrant === 2 || quadrant === 4 ?
            `${document.documentElement.clientWidth - right}px` : '',
        maxHeight: `${document.documentElement.clientHeight - bottom * 0.9}px`,
        maxWidth: `${quadrant === 1 || quadrant === 3 ?
            (document.documentElement.clientWidth - left) * 0.9 :
            (document.documentElement.clientWidth - right) * 0.9}px`,
    };
}

/**
 * Manages the position of an HTML element in relation to a specified anchor element.
 */
function calculateFixedElementPosition(strategy, anchor, panel) {
    const { styles, options } = strategy.position(anchor, panel);
    const config = strategy.config;
    if (!config.scrollableX) {
        delete styles.maxWidth;
    }
    if (!config.scrollableY) {
        delete styles.maxHeight;
    }
    return { styles, options, anchor };
}
function setStyles(relativeElement, styles, { position, direction }) {
    // remove and re-add classes
    removeDescriptorClasses(relativeElement);
    relativeElement.classList.add(`-cds-position-${position}`);
    relativeElement.classList.add(`-cds-direction-${direction}`);
    Object.entries(styles).forEach(([key, value]) => {
        relativeElement.style[key] = value;
    });
}
function getStyles$1(element) {
    return Object.entries(element.style)
        .filter(([key]) => !isNaN(+key))
        .map(([_, styleProp]) => styleProp)
        .reduce((acc, styleProp) => ({
        ...acc,
        [styleProp]: element.style.getPropertyValue(styleProp)
    }), {});
}
function removeStyles(element, originalStyles) {
    removeDescriptorClasses(element);
    const stylesToRemove = ['top', 'bottom', 'left', 'right', 'margin', 'min-width', 'max-width', 'max-height'];
    stylesToRemove.forEach(style => element.style.removeProperty(style));
    Object.entries(originalStyles || {}).forEach(([key, value]) => element.style.setProperty(key, value));
}
function removeDescriptorClasses(element) {
    const directions = ['top', 'bottom', 'left', 'right'];
    directions.forEach(direction => {
        element.classList.remove(`-cds-position-${direction}`);
        element.classList.remove(`-cds-direction-${direction}`);
    });
}
function disableScroll() {
    document.body.classList.add('-disable-scroll');
}
function enableScroll() {
    document.body.classList.remove('-disable-scroll');
}

class DropdownConfig {
}

class QuadrantPositionStrategy extends FixedPositionStrategy {
    /**
     * @param anchor - The element that the panel is anchored to.
     * @param adjacent - When true, panel drops to the right or left instead of up/down.
     * @param flexPlacement - Forces adjacent behaviour if the panel is too large to drop up/down.
     */
    constructor(config) {
        super(config);
    }
    position(anchor, panel) {
        const rect = anchor instanceof Element
            ? anchor.getBoundingClientRect()
            : new DOMRect(anchor.x, anchor.y);
        const quadrant = getQuadrant(rect);
        const styles = {};
        const x = quadrant === 1 || quadrant === 3 ? 'right' : 'left';
        const y = quadrant === 1 || quadrant === 2 ? 'bottom' : 'top';
        const getOptions = (adjacent) => ({
            position: adjacent ? x : y,
            direction: adjacent ? y : x,
            quadrant
        });
        if (this.config.flexPlacement) {
            // Flex placement is necessary to prevent the viewport from cutting off non-scrolling dropdown panels.
            // In order to do this, we need to figure out the dropdown panel height, which is not available until the panel is
            // visible (usually handled at the component level).
            // So we need to briefly change the display property value - while keeping the panel invisible to the user - in order to
            // obtain the dropdown panel's height.
            const oldVisibilityValue = panel.style.visibility;
            const oldDisplayValue = panel.style.display;
            panel.style.visibility = 'hidden';
            panel.style.display = 'flex';
            const panelHeight = panel.clientHeight;
            panel.style.visibility = oldVisibilityValue;
            panel.style.display = oldDisplayValue;
            const isTopQuadrant = quadrant === 1 || quadrant === 2;
            const availableSpaceForPanel = isTopQuadrant
                ? document.documentElement.clientHeight - rect.bottom : rect.top;
            if (availableSpaceForPanel < panelHeight) {
                return {
                    styles: {
                        ...styles,
                        ...getFlexStyles(quadrant, rect, panelHeight)
                    },
                    options: getOptions(true),
                    anchor
                };
            }
        }
        if (this.config.adjacent) {
            return {
                styles: {
                    ...styles,
                    ...getAdjacentStyles(quadrant, rect)
                },
                options: getOptions(true),
                anchor
            };
        }
        return {
            styles: {
                ...styles,
                ...getStyles(quadrant, rect)
            },
            options: getOptions(false),
            anchor
        };
    }
}
function getFlexStyles(quadrant, { left, right }, panelHeight) {
    const remainder = document.documentElement.clientHeight - panelHeight;
    switch (quadrant) {
        case 1:
        default:
            return {
                top: `${remainder / 2}px`,
                left: `${right}px`,
                maxHeight: `${document.documentElement.clientHeight * 0.9}px`,
                maxWidth: `${(document.documentElement.clientWidth - right) * 0.9}px`,
            };
            break;
        case 2:
            return {
                top: `${remainder / 2}px`,
                right: `${document.documentElement.clientWidth - left}px`,
                maxHeight: `${document.documentElement.clientHeight * 0.9}px`,
                maxWidth: `${left * 0.9}px`,
            };
            break;
        case 3:
            return {
                bottom: `${remainder / 2}px`,
                left: `${right}px`,
                maxHeight: `${document.documentElement.clientHeight * 0.9}px`,
                maxWidth: `${(document.documentElement.clientWidth - right) * 0.9}px`,
            };
            break;
        case 4:
            return {
                bottom: `${remainder / 2}px`,
                right: `${document.documentElement.clientWidth - left}px`,
                maxHeight: `${document.documentElement.clientHeight * 0.9}px`,
                maxWidth: `${left * 0.9}px`,
            };
            break;
    }
}
function getAdjacentStyles(quadrant, { left, right, top, bottom }) {
    switch (quadrant) {
        case 1:
        default:
            return {
                top: `${top}px`,
                left: `${right}px`,
                maxHeight: `${(document.documentElement.clientHeight - top) * 0.9}px`,
                maxWidth: `${(document.documentElement.clientWidth - right) * 0.9}px`,
            };
            break;
        case 2:
            return {
                top: `${top}px`,
                right: `${document.documentElement.clientWidth - left}px`,
                maxHeight: `${(document.documentElement.clientHeight - top) * 0.9}px`,
                maxWidth: `${left * 0.9}px`,
            };
            break;
        case 3:
            return {
                bottom: `${document.documentElement.clientHeight - bottom}px`,
                left: `${right}px`,
                maxHeight: `${bottom * 0.9}px`,
                maxWidth: `${(document.documentElement.clientWidth - right) * 0.9}px`,
            };
            break;
        case 4:
            return {
                bottom: `${document.documentElement.clientHeight - bottom}px`,
                right: `${document.documentElement.clientWidth - left}px`,
                maxHeight: `${bottom * 0.9}px`,
                maxWidth: `${left * 0.9}px`,
            };
            break;
    }
}
function getStyles(quadrant, { left, right, top, bottom }) {
    switch (quadrant) {
        case 1:
        default:
            return {
                top: `${bottom}px`,
                left: `${left}px`,
                maxHeight: `${(document.documentElement.clientHeight - bottom) * 0.9}px`,
                maxWidth: `${(document.documentElement.clientWidth - left) * 0.9}px`,
            };
            break;
        case 2:
            return {
                top: `${bottom}px`,
                right: `${document.documentElement.clientWidth - right}px`,
                maxHeight: `${(document.documentElement.clientHeight - bottom) * 0.9}px`,
                maxWidth: `${right * 0.9}px`,
            };
            break;
        case 3:
            return {
                bottom: `${document.documentElement.clientHeight - top}px`,
                left: `${left}px`,
                maxHeight: `${top * 0.9}px`,
                maxWidth: `${(document.documentElement.clientWidth - left) * 0.9}px`,
            };
            break;
        case 4:
            return {
                bottom: `${document.documentElement.clientHeight - top}px`,
                right: `${document.documentElement.clientWidth - right}px`,
                maxHeight: `${top * 0.9}px`,
                maxWidth: `${right * 0.9}px`,
            };
            break;
    }
}

class PositionResult {
}

const isNullOrUndefined = (value) => value === null || value === undefined;
const isBoolean = (value) => value === true || value === false;

/**
 * Generated bundle index. Do not edit.
 */

export { CdsCalendarDate, CdsDataFilter, CdsDataPage, CdsDataSort, CdsGroupedListItem, CdsHighlightableComponent, CdsInputConfig, CdsInputValidationOptions, CdsListItem, CdsSortDirection, CdsTraversableComponent, DirectionalPositionStrategy, DropdownConfig, FixedPositionStrategy, GridThresholdWarning, MutationObserverSubject, PositionResult, QuadrantPositionStrategy, ResizeObserverSubject, appendComponentToBody, calculateCalendarDates, calculateCalendarDatesForWeek, calculateCalendarWeekdays, calculateFixedElementPosition, combineDateparts, convertToPixels, createComponent, dateComparatorFn, disableScroll, enableScroll, flatten, getDateObject, getDateObjectWithHours, getDateParts, getLocaleWeekStart, getMonths, getOptionsFromColumn, getQuadrant, getStyles$1 as getStyles, getWeekDays, getYears, isBoolean, isNavigationKey, isNullOrUndefined, pluralize, removeComponentFromBody, removeStyles, setStyles };
